import { FC, useMemo } from 'react';
import { useMantineTheme } from '@mantine/core';
import { BsPlusLg } from 'react-icons/bs';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
} from '@xyflow/react';
import { useGraphContext } from '../../context';
import { DrawerKind, NodePickerContextType } from '../../store/ui';

export const Edge: FC<EdgeProps> = (edge) => {
  const [openDrawer, setFocusNodeId] = useGraphContext(({ ui }) => [
    ui.openDrawer,
    ui.setFocusNodeId,
  ]);

  const theme = useMantineTheme();
  const isHovered = Boolean(edge.data?.isHovered);
  const [edgePath, labelX, labelY] = useMemo(
    () =>
      getSmoothStepPath({
        sourceX: edge.sourceX,
        sourceY: edge.sourceY,
        targetX: edge.targetX,
        targetY: edge.targetY,
        sourcePosition: edge.sourcePosition,
        targetPosition: edge.targetPosition,
        borderRadius: 20,
      }),
    [
      edge.sourcePosition,
      edge.sourceX,
      edge.sourceY,
      edge.targetPosition,
      edge.targetX,
      edge.targetY,
    ]
  );
  const backgroundColor =
    theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white;
  const borderColor =
    theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];
  const iconColor =
    theme.colorScheme === 'dark' ? theme.colors.gray[2] : theme.colors.gray[7];
  const edgeStyle = {
    stroke: theme.colors.gray[4],
    strokeWidth: 2,
    zIndex: 10,
    transition: 'stroke 220ms ease, stroke-width 220ms ease',
  };
  const hoveredEdgeStyle = {
    ...edgeStyle,
    stroke: theme.colors[theme.primaryColor][5],
    strokeWidth: 3,
    zIndex: 300,
  };

  const handleInsert = () => {
    setFocusNodeId(edge.target);
    openDrawer({
      kind: DrawerKind.NodePicker,
      context: {
        mode: NodePickerContextType.InsertOnEdge,
        edgeId: edge.id,
      },
    });
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={edge.markerEnd}
        style={isHovered ? hoveredEdgeStyle : edgeStyle}
      />
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={18}
        style={{ pointerEvents: 'stroke' }}
        onClick={handleInsert}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: isHovered ? 'auto' : 'none',
            width: 18,
            height: 18,
            borderRadius: 999,
            backgroundColor,
            border: `1px solid ${borderColor}`,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: theme.shadows.xs,
            opacity: isHovered ? 1 : 0,
            transformOrigin: 'center',
            transition: 'opacity 120ms ease',
            cursor: 'pointer',
            zIndex: 2,
          }}
          onClick={handleInsert}
        >
          <BsPlusLg size={10} />
        </div>
      </EdgeLabelRenderer>
    </>
  );
};
